/**
 * The client's cell backend, as one seam (`CellBackendFactory`): what the
 * client hands it (its typed API, sync client, encodings and listener-error
 * sink) and the two backends it builds — the cell-caching backend the
 * client's `SdkBackendAdapter` delegates its residual calls to, and the rows
 * model's `Backend`. The default (`CellBackendFactory.HTTP`) is the HTTP
 * composition every client has always used.
 *
 * SDK-internal: no entry exports it. A test (or the in-memory proof store)
 * swaps the composition for one client by registering a factory against
 * that client's config before building it (`withCellBackend`); the published
 * `ClientConfig` gains no option.
 */
import type { Backend, Option } from "@tento-lona/sheets";
import type { TypedApi } from "@tento-core/typed-api";
import { type EncodingRegistry, RowsHttpBackend } from "@tento-lona/sheets/platform";
import type { Network } from "../network.ts";
import type { SyncClient } from "../services/sync/sync-client.ts";
import { type CellCachingBackend, SyncHttpBackend } from "../sync/sync-backend.ts";
import type { ListenerErrorHandler } from "./client-seams.ts";

/** What the client hands its cell-backend factory: its transport and the one listener-error sink. */
export interface CellBackendDeps {
  readonly api: TypedApi<Network.Routes>;
  readonly syncClient: Option<SyncClient>;
  readonly encodings: EncodingRegistry;
  readonly listenerErrors: ListenerErrorHandler;
}

/** The two backends a factory builds. */
export interface CellBackends {
  /** The cell-caching backend (`SdkBackendAdapter` delegates its residual calls to it). */
  readonly legacy: CellCachingBackend;
  /** The rows model's backend (row-field writes, the migrated cell writes). */
  readonly sheets: Backend;
}

/** Builds the client's cell backends; called once, while the client is built. */
export type CellBackendFactory = (deps: CellBackendDeps) => CellBackends;

/** The shipped factory. */
export namespace CellBackendFactory {
  /** Today's composition: `SyncHttpBackend` over the client's API, sync client and sink, and `RowsHttpBackend`. */
  export const HTTP: CellBackendFactory = (deps) => ({
    legacy: new SyncHttpBackend(deps.api, deps.syncClient, deps.encodings, deps.listenerErrors),
    sheets: new RowsHttpBackend(deps.api),
  });
}

/**
 * The factories registered against client configs (`withCellBackend`),
 * weakly: an entry lives exactly as long as the config object it is keyed by.
 */
const registered = new WeakMap<object, CellBackendFactory>();

/**
 * `config`, with `factory` as the cell-backend factory of the client built
 * from it (SDK tests and the in-memory proof store; not a published option).
 */
export function withCellBackend<C extends object>(config: C, factory: CellBackendFactory): C {
  registered.set(config, factory);
  return config;
}

/** The factory registered for `config`, else `CellBackendFactory.HTTP`. */
export function cellBackendFactoryOf(config: object): CellBackendFactory {
  return registered.get(config) ?? CellBackendFactory.HTTP;
}
